import { contactBlock } from "./legal";

export const termsAndConditions = {
  title: "Terms & Conditions",
  updated: "14 September 2026",
  heroCopy:
    "These Terms govern your access to and use of the Bytevora website, as well as any enquiries, quotations, proposals, payments, or services provided through or in connection with it.",
  toc: [
    { label: "Website Use", id: "use-of-this-website" },
    { label: "Services", id: "service-information" },
    { label: "Pricing & Payments", id: "pricing" },
    { label: "Refunds", id: "refunds-and-cancellations" },
    { label: "Project Delivery", id: "delivery-of-services" },
    { label: "Intellectual Property", id: "intellectual-property" },
    { label: "Third-Party Services", id: "third-party-software-and-services" },
    { label: "AI & Technology", id: "artificial-intelligence-and-automated-systems" },
    { label: "Liability", id: "limitation-of-liability" },
    { label: "Termination", id: "suspension-or-termination" },
    { label: "Governing Law", id: "governing-law-and-jurisdiction" },
    { label: "Contact", id: "contact-us" },
  ],
  intro: [
    "By accessing or using our website, submitting an enquiry, requesting services, or making a payment to Bytevora, you agree to these Terms.",
    "If you do not agree with these Terms, please do not use the website or our services.",
  ],
  sections: [
    {
      id: "about-bytevora",
      title: "About Bytevora",
      blocks: [
        {
          type: "p",
          segments: [
            { t: "Bytevora IT Solutions Private Limited", strong: true },
            { t: " is a private company limited by shares incorporated in India." },
          ],
        },
        { type: "list", items: ["Registered Office: D-46, Vibhuti Khand, Gomtinagar, Lucknow, Uttar Pradesh – 226010, India", "CIN: U62091UP2026PTC251666"] },
        { type: "p", text: "Bytevora provides technology and digital services, including software development, web and mobile development, cloud and infrastructure services, AI and automation solutions, digital transformation, technology consulting, and related services." },
      ],
    },
    {
      id: "use-of-this-website",
      title: "Use of This Website",
      blocks: [
        { type: "p", text: "You may use this website only for lawful purposes." },
        { type: "p", text: "You agree not to:" },
        {
          type: "list",
          items: [
            "Use the website in violation of applicable law",
            "Attempt to gain unauthorized access to the website, servers, systems, or accounts",
            "Introduce malicious software, viruses, scripts, or harmful code",
            "Interfere with the security or operation of the website",
            "Scrape, copy, reproduce, or exploit website content unlawfully",
            "Misrepresent your identity or affiliation",
            "Submit false, fraudulent, abusive, or misleading information",
            "Use the website to infringe the rights of Bytevora or any third party",
          ],
        },
        { type: "p", text: "We may restrict or block access where we reasonably believe the website is being misused." },
      ],
    },
    {
      id: "service-information",
      title: "Service Information",
      blocks: [
        { type: "p", text: "Information about Bytevora’s services, capabilities, technologies, and processes is provided for general business and informational purposes." },
        { type: "p", text: "Service descriptions on the website do not automatically create a binding obligation to provide any particular service, feature, outcome, timeline, or deliverable." },
        { type: "p", text: "The final scope of any project will be determined through a separate proposal, quotation, statement of work, order, service agreement, contract, or written confirmation where applicable." },
        { type: "p", text: "If there is a conflict between these website Terms and a separately executed client agreement, the signed client agreement will generally govern the relevant services." },
      ],
    },
    {
      id: "project-enquiries",
      title: "Project Enquiries",
      blocks: [
        { type: "p", text: "Submitting a contact form, project enquiry, email, or request for quotation does not create a client relationship or contractual obligation." },
        { type: "p", text: "Bytevora may review the enquiry, request additional information, decline the enquiry, propose a discovery discussion, issue a quotation or proposal, or recommend a different technical approach." },
        { type: "p", text: "A project becomes confirmed only after the applicable commercial terms are accepted and any required payment or contractual formalities are completed." },
      ],
    },
    {
      id: "quotations-and-proposals",
      title: "Quotations and Proposals",
      blocks: [
        { type: "p", text: "Unless otherwise stated, quotations and proposals issued by Bytevora may be based on the information available at the time, subject to clarification of scope, subject to technical review, valid only for the period stated in the quotation, and revised if requirements change." },
        { type: "p", text: "Any work outside the agreed scope may require additional charges, revised timelines, a change request, a new quotation, or written approval." },
      ],
    },
    {
      id: "pricing",
      title: "Pricing",
      blocks: [
        { type: "p", text: "Prices shown on the website, if any, are indicative unless expressly stated otherwise." },
        { type: "p", text: "Final pricing may depend on factors including project scope, complexity, features, integrations, timeline, infrastructure, third-party services, licensing, support requirements, custom development, taxes, and regulatory requirements." },
        { type: "p", text: "Bytevora reserves the right to revise pricing before a project is formally accepted." },
      ],
    },
    {
      id: "taxes",
      title: "Taxes",
      blocks: [
        { type: "p", text: "Prices may be exclusive of applicable taxes unless stated otherwise." },
        { type: "p", text: "Applicable taxes, duties, levies, GST, or similar charges may be added where legally required." },
        { type: "p", text: "Clients are responsible for providing accurate billing and tax information where necessary." },
      ],
    },
    {
      id: "payments",
      title: "Payments",
      blocks: [
        { type: "p", text: "Payment terms will normally be stated in the applicable quotation, invoice, proposal, contract, or order confirmation." },
        { type: "p", text: "Depending on the project, Bytevora may require advance payment, milestone payments, subscription payments, retainer payments, full payment before delivery, or recurring payments." },
        { type: "p", text: "Services may not commence until the required initial payment has been received." },
      ],
    },
    {
      id: "payment-gateways",
      title: "Payment Gateways",
      blocks: [
        { type: "p", text: "Online payments may be processed through third-party payment gateways." },
        { type: "p", text: "Bytevora does not control the payment gateway’s availability, authentication process, transaction approval, bank authorization, security procedures, or processing time." },
        { type: "p", text: "Payments may be subject to the relevant provider’s terms and policies." },
        { type: "p", text: "Bytevora may receive transaction-related details such as payment status, amount, currency, billing information, and reference number." },
      ],
    },
    {
      id: "failed-or-delayed-payments",
      title: "Failed or Delayed Payments",
      blocks: [
        { type: "p", text: "If a payment fails, is reversed, disputed, delayed, or remains unpaid, Bytevora may, where appropriate, pause work, suspend access to services, withhold delivery, delay project milestones, suspend support, or cancel or terminate the affected service." },
        { type: "p", text: "Any such action will be subject to the relevant agreement and applicable law." },
      ],
    },
    {
      id: "refunds-and-cancellations",
      title: "Refunds and Cancellations",
      blocks: [
        {
          type: "p",
          segments: [
            { t: "Refunds and cancellations are governed by Bytevora’s " },
            { link: "Refund & Cancellation Policy", to: "/refund-cancellation-policy" },
            { t: " and any project-specific agreement." },
          ],
        },
        { type: "p", text: "Because technology services often involve customized work, allocated development time, infrastructure, or third-party expenses, refund eligibility may depend on project stage, work already completed, non-refundable third-party costs, resources already allocated, contract terms, and the nature of the service." },
        {
          type: "p",
          segments: [
            { t: "Please review the " },
            { link: "Refund & Cancellation Policy", to: "/refund-cancellation-policy" },
            { t: " before making a payment." },
          ],
        },
      ],
    },
    {
      id: "delivery-of-services",
      title: "Delivery of Services",
      blocks: [
        { type: "p", text: "Bytevora primarily provides digital and technology services." },
        { type: "p", text: "Delivery may include software, source code, design files, credentials, documentation, access to hosted systems, cloud deployments, configurations, reports, digital files, technical consultation, and other agreed deliverables." },
        { type: "p", text: "Delivery timelines depend on the agreed project scope and client cooperation." },
        {
          type: "p",
          segments: [
            { t: "For more information, review our " },
            { link: "Shipping & Delivery Policy", to: "/shipping-delivery-policy" },
            { t: "." },
          ],
        },
      ],
    },
    {
      id: "client-responsibilities",
      title: "Client Responsibilities",
      blocks: [
        { type: "p", text: "Clients are responsible for providing accurate and timely information necessary to complete the project." },
        { type: "p", text: "This may include business requirements, content, branding assets, technical access, server credentials, API credentials, third-party account access, feedback, approvals, and legal or compliance information." },
        { type: "p", text: "Delays caused by missing information, approvals, access, or feedback may affect project timelines." },
      ],
    },
    {
      id: "changes-in-scope",
      title: "Changes in Scope",
      blocks: [
        { type: "p", text: "If the client requests functionality or work outside the original agreed scope, Bytevora may treat it as a change request." },
        { type: "p", text: "Such changes may affect cost, timeline, technical architecture, resource allocation, and delivery date." },
        { type: "p", text: "Additional work will generally require written approval before implementation." },
      ],
    },
    {
      id: "project-timelines",
      title: "Project Timelines",
      blocks: [
        { type: "p", text: "Any estimated timeline is based on the project information available at the time." },
        { type: "p", text: "Timelines may change due to scope changes, delayed client feedback, missing content, third-party dependencies, API limitations, infrastructure issues, unexpected technical problems, external service outages, or events beyond reasonable control." },
        { type: "p", text: "Bytevora will make reasonable efforts to communicate material delays." },
      ],
    },
    {
      id: "acceptance-and-approval",
      title: "Acceptance and Approval",
      blocks: [
        { type: "p", text: "Where deliverables are submitted for review, the client may be required to review and approve them within a reasonable period or within the timeline specified in the applicable agreement." },
        { type: "p", text: "Failure to provide timely feedback may delay the project." },
        { type: "p", text: "Project-specific acceptance criteria may be defined separately." },
      ],
    },
    {
      id: "intellectual-property",
      title: "Intellectual Property",
      blocks: [
        { type: "p", text: "Ownership of project-specific intellectual property will be governed by the applicable quotation, proposal, contract, or written agreement." },
        { type: "p", text: "Unless otherwise agreed:" },
        {
          type: "list",
          items: [
            "Bytevora retains ownership of its pre-existing intellectual property",
            "Bytevora may retain reusable tools, libraries, methods, templates, frameworks, utilities, and know-how",
            "Third-party software remains subject to the rights of its respective owner",
            "Open-source components remain subject to their applicable licences",
          ],
        },
        { type: "p", text: "Transfer of ownership of custom deliverables may be conditional upon full payment." },
      ],
    },
    {
      id: "client-materials",
      title: "Client Materials",
      blocks: [
        { type: "p", text: "The client retains ownership of materials supplied to Bytevora, subject to any rights required for Bytevora to perform the services." },
        { type: "p", text: "By providing materials, the client represents that they have the necessary rights or permissions to use and provide them." },
        { type: "p", text: "The client is responsible for ensuring that supplied text, images, logos, data, documents, media, software, trademarks, and other content do not unlawfully infringe third-party rights." },
      ],
    },
    {
      id: "third-party-software-and-services",
      title: "Third-Party Software and Services",
      blocks: [
        { type: "p", text: "Projects may rely on third-party products or services such as hosting platforms, cloud providers, APIs, payment gateways, SaaS tools, libraries, open-source software, plugins, communication services, or AI providers." },
        { type: "p", text: "Such third-party services are subject to their own terms, pricing, licences, availability, limitations, and privacy policies." },
        { type: "p", text: "Bytevora is not responsible for changes, outages, restrictions, pricing revisions, or discontinuation by independent third-party providers." },
      ],
    },
    {
      id: "open-source-software",
      title: "Open-Source Software",
      blocks: [
        { type: "p", text: "Bytevora may use open-source software where technically appropriate." },
        { type: "p", text: "Open-source components remain subject to their respective licences." },
        { type: "p", text: "Nothing in these Terms transfers ownership of open-source software to either party." },
      ],
    },
    {
      id: "hosting-cloud-and-infrastructure",
      title: "Hosting, Cloud, and Infrastructure",
      blocks: [
        { type: "p", text: "Where Bytevora provides or manages hosting, cloud, infrastructure, or deployment services, availability may depend on third-party infrastructure providers." },
        { type: "p", text: "Bytevora does not guarantee uninterrupted service unless an explicit service-level commitment is included in a separate written agreement." },
        { type: "p", text: "Planned maintenance, emergency maintenance, third-party outages, and security events may affect availability." },
      ],
    },
    {
      id: "artificial-intelligence-and-automated-systems",
      title: "Artificial Intelligence and Automated Systems",
      blocks: [
        { type: "p", text: "Where AI, machine learning, automation, or generative technologies are incorporated into a project, clients acknowledge that such systems may produce inaccurate or incomplete outputs, require human review, depend on third-party providers, change over time, be subject to usage restrictions, or require responsible implementation." },
        { type: "p", text: "Bytevora does not guarantee that AI-generated outputs will always be accurate or suitable for a specific legal, medical, financial, or high-risk decision." },
      ],
    },
    {
      id: "security",
      title: "Security",
      blocks: [
        { type: "p", text: "Bytevora uses reasonable technical and organizational measures where appropriate." },
        { type: "p", text: "However, no software system, website, network, cloud environment, or internet-based service can be guaranteed to be completely secure." },
        { type: "p", text: "Clients are responsible for maintaining the security of their accounts, passwords, credentials, devices, third-party services, and access permissions." },
        { type: "p", text: "Security responsibilities may be defined further in the applicable project agreement." },
      ],
    },
    {
      id: "backups",
      title: "Backups",
      blocks: [
        { type: "p", text: "Unless expressly agreed in writing, clients should not assume Bytevora provides indefinite or comprehensive backup retention for their data, systems, or content." },
        { type: "p", text: "Where backup services are included, the scope and retention terms should be defined separately." },
        { type: "p", text: "Clients should maintain appropriate independent backups where reasonably necessary." },
      ],
    },
    {
      id: "confidentiality",
      title: "Confidentiality",
      blocks: [
        { type: "p", text: "Where Bytevora and a client exchange confidential business or technical information, each party should take reasonable steps to protect such information." },
        { type: "p", text: "More detailed confidentiality obligations may be set out in a contract, NDA, proposal, statement of work, or separate written agreement." },
      ],
    },
    {
      id: "portfolio-and-publicity",
      title: "Portfolio and Publicity",
      blocks: [
        { type: "p", text: "Bytevora will not automatically claim the right to publicly display confidential client information." },
        { type: "p", text: "Any use of client names, logos, testimonials, screenshots, or project details for marketing or portfolio purposes should be subject to client permission or the relevant agreement." },
      ],
    },
    {
      id: "support-and-maintenance",
      title: "Support and Maintenance",
      blocks: [
        { type: "p", text: "Support, maintenance, monitoring, updates, bug fixes, or post-launch assistance are included only where specified in the relevant agreement or service package." },
        { type: "p", text: "Unless expressly agreed otherwise, completion of the initial project does not create an indefinite obligation to provide ongoing support." },
      ],
    },
    {
      id: "warranties",
      title: "Warranties",
      blocks: [
        { type: "p", text: "Bytevora will use reasonable professional care in providing services." },
        { type: "p", text: "However, unless expressly stated in a separate agreement, services and website information are provided without guarantees that software will be completely error-free, services will be uninterrupted, every defect can be eliminated, third-party systems will remain available, or any particular commercial result will be achieved." },
        { type: "p", text: "Nothing in this section limits rights that cannot legally be excluded." },
      ],
    },
    {
      id: "no-guarantee-of-business-results",
      title: "No Guarantee of Business Results",
      blocks: [
        { type: "p", text: "Bytevora does not guarantee specific revenue, profit, traffic, rankings, conversion rates, user growth, investment, market success, operational savings, or business outcomes unless expressly stated in a separate written agreement." },
        { type: "p", text: "Technology can support business outcomes, but results depend on many factors beyond Bytevora’s control." },
      ],
    },
    {
      id: "limitation-of-liability",
      title: "Limitation of Liability",
      blocks: [
        { type: "p", text: "To the maximum extent permitted by applicable law, Bytevora will not be liable for indirect, incidental, special, consequential, punitive, or similar losses arising from use of the website or services where such liability can lawfully be limited." },
        { type: "p", text: "Where a separate client agreement exists, liability relating to paid services will be governed primarily by that agreement." },
        { type: "p", text: "Nothing in these Terms excludes liability where exclusion is prohibited by law." },
      ],
    },
    {
      id: "indemnity",
      title: "Indemnity",
      blocks: [
        { type: "p", text: "To the extent permitted by law, you agree to be responsible for claims, losses, or liabilities resulting from your unlawful use of the website, materials you provide without proper rights, your breach of these Terms, misuse of delivered services, or violation of third-party rights." },
        { type: "p", text: "Any project-specific indemnity obligations may be stated separately in the relevant agreement." },
      ],
    },
    {
      id: "suspension-or-termination",
      title: "Suspension or Termination",
      blocks: [
        { type: "p", text: "Bytevora may suspend or terminate access to services where reasonably necessary due to non-payment, material breach, fraud, abuse, security risks, illegal activity, misuse of systems, or serious contractual violation." },
        { type: "p", text: "Where practical, notice may be provided according to the applicable agreement." },
      ],
    },
    {
      id: "website-availability",
      title: "Website Availability",
      blocks: [
        { type: "p", text: "We may modify, suspend, restrict, or discontinue all or part of the website at any time." },
        { type: "p", text: "We do not guarantee that the website will always be available, error-free, secure, or uninterrupted." },
        { type: "p", text: "Temporary downtime may occur due to maintenance, upgrades, third-party outages, or technical issues." },
      ],
    },
    {
      id: "external-links",
      title: "External Links",
      blocks: [
        { type: "p", text: "The website may contain links to third-party websites." },
        { type: "p", text: "Bytevora is not responsible for the content, privacy practices, security, availability, or accuracy of external websites." },
        { type: "p", text: "External links do not necessarily imply endorsement." },
      ],
    },
    {
      id: "privacy",
      title: "Privacy",
      blocks: [
        {
          type: "p",
          segments: [
            { t: "Use of personal information is governed by our " },
            { link: "Privacy Policy", to: "/privacy-policy" },
            { t: ". Use of cookies and similar technologies is explained in our " },
            { link: "Cookie Policy", to: "/cookie-policy" },
            { t: "." },
          ],
        },
        { type: "p", text: "These policies should be read together with these Terms." },
      ],
    },
    {
      id: "force-majeure",
      title: "Force Majeure",
      blocks: [
        { type: "p", text: "Bytevora will not be responsible for delays or failures caused by events beyond its reasonable control, including natural disasters, government actions, internet failures, power outages, cyber incidents, war, civil unrest, labour disruption, third-party service failures, cloud outages, telecommunications failures, or other comparable events, where permitted by law." },
      ],
    },
    {
      id: "governing-law-and-jurisdiction",
      title: "Governing Law and Jurisdiction",
      blocks: [
        { type: "p", text: "These Terms shall be governed by the laws of India." },
        { type: "p", text: "Unless otherwise agreed in writing or required by applicable law, disputes relating to these Terms or the website shall be subject to the appropriate courts having jurisdiction in Lucknow, Uttar Pradesh, India." },
        { type: "p", text: "For specific client engagements, dispute resolution terms may be defined separately in the relevant agreement." },
      ],
    },
    {
      id: "severability",
      title: "Severability",
      blocks: [
        { type: "p", text: "If any provision of these Terms is found to be invalid or unenforceable, the remaining provisions will continue to remain in effect to the extent permitted by law." },
      ],
    },
    {
      id: "no-waiver",
      title: "No Waiver",
      blocks: [
        { type: "p", text: "Failure by Bytevora to enforce any provision of these Terms does not constitute a waiver of that provision or any other right." },
      ],
    },
    {
      id: "entire-website-terms",
      title: "Entire Website Terms",
      blocks: [
        {
          type: "p",
          segments: [
            { t: "These Terms, together with the " },
            { link: "Privacy Policy", to: "/privacy-policy" },
            { t: ", " },
            { link: "Cookie Policy", to: "/cookie-policy" },
            { t: ", " },
            { link: "Refund & Cancellation Policy", to: "/refund-cancellation-policy" },
            { t: ", " },
            { link: "Shipping & Delivery Policy", to: "/shipping-delivery-policy" },
            { t: ", and " },
            { link: "Disclaimer", to: "/disclaimer" },
            { t: ", form the general legal framework governing use of the Bytevora website." },
          ],
        },
        { type: "p", text: "Project-specific contracts, proposals, quotations, statements of work, or service agreements may include additional or different terms." },
      ],
    },
    {
      id: "changes-to-these-terms",
      title: "Changes to These Terms",
      blocks: [
        { type: "p", text: "Bytevora may update these Terms from time to time to reflect changes in services, website features, business practices, technology, legal requirements, or payment processes." },
        { type: "p", text: "The latest version will be published on this page with an updated revision date." },
        { type: "p", text: "Continued use of the website after an update constitutes acknowledgement of the revised Terms." },
      ],
    },
    {
      id: "contact-us",
      title: "Contact Us",
      blocks: [
        { type: "p", text: "For questions regarding these Terms & Conditions, please contact:" },
        contactBlock,
      ],
    },
  ],
};
